import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "../../common/AppBar";
import Snackbar from "../../common/Snackbar";

import { getFilterdProcedureParts } from "../../redux/partSlice";
import { getProcedureRevisionsForPart } from "../../redux/procedureRevisionSlice";
import {
  deleteProcedureTemplateRule,
  getProcedureTemplateRuleById,
  updateProcedureTemplateRuleMultiData,
} from "../../redux/procedureTemplateRulesSlice";
import { getAllTypes } from "../../redux/typeSlice";
import { getAllUnits } from "../../redux/unitSlice";
import PartsPopup from "../../common/AppSpecificConpnents/PartsTable/PartsPopup";
import AppTable from "../../common/AppTable/AppTable";
import { SortTableData } from "../../common/AppTable/SortTableData";
import Loader from "../../common/Loader";
import RenderHtml from "../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import { buildPermissionContext } from "../../util/permissions";

const EditProcedureTemplateRule = () => {
  const columnData = [
    {
      headName: "Document Number",
      field: "procedureNumber",
      required: true,
      sort: true,
      width: "150px",
    },
    {
      headName: "Description",
      field: "description",
      sort: true,
    },
    {
      headName: "Default Template",
      field: "is_default_template",
      sort: true,
      width: "80px",
    },
    { headName: "Action", field: "action", textCenter: true, width: "120px" },
  ];
  const tableRef = useRef();
  const [localSortConfig, setLocalSortConfig] = useState({
    key: null,
    direction: null,
  });
  const [sortedTempRules, setSortedTempRules] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const navigate = useNavigate();

  const noblankRef = useRef(null);

  const [editingRuleIndex, setEditingRuleIndex] = useState(null);
  const [editedRule, setEditedRule] = useState({});

  const location = useLocation();
  const { rule } = location.state || {};
  const dispatch = useDispatch();

  const { rule: fetchedRule } = useSelector(
    (state) => state.procedureTemplateRule
  );
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  const [procedureType, setProcedureType] = useState("");
  const [rules, setRules] = useState([]);
  const [isPartsModalOpen, setPartsModalOpen] = useState(false);

  const [editingRowIndex, setEditingRowIndex] = useState(null);

  useEffect(() => {
    dispatch(getFilterdProcedureParts({ limit: 100, offset: 0 }));
    dispatch(getAllUnits({ limit: 1000, offset: 0 }));
    dispatch(getAllTypes({ limit: 1000, offset: 0 }));

    if (rule?.type_id) {
      dispatch(getProcedureTemplateRuleById(rule.type_id));
    }
  }, [dispatch, rule]);

  useEffect(() => {
    if (fetchedRule || rule?.name) {
      setProcedureType(rule?.name || "");
      const fetchedParts =
        fetchedRule?.parts?.map((part) => ({
          procedure_template_id: part.procedure_template_id,
          type_id_ref: fetchedRule.type_id,
          procedure_id_ref: part.part_id,
          procedureNumber: part.part_number,
          description: part.description,
          is_default_template: part.is_default_template,
          isNew: false, // Mark as existing part
        })) || [];
      const arr = [
        {
          procedureNumber: "Blank Template",
          description: "A Document with No Content",
          blank_template_row: true,
          isNew: false, // Mark as default row
        },
        ...fetchedParts,
      ];

      if (
        fetchedRule?.is_blank_template_permitted === true &&
        fetchedParts.filter((part) => part.is_default_template)?.length === 0
      ) {
        arr[0].is_default_template = true;
      }

      const defIndex = arr.findIndex((r) => r.is_default_template);
      setRules(arr);
      if (defIndex !== -1) {
        setDefaultIndex(defIndex);
      } else {
        setDefaultIndex(0);
      }
    }
  }, [fetchedRule, rule?.name, location.state?.rule]);

  const handleEditProcedureNumber = (index) => {
    setEditingRowIndex(index);
    setPartsModalOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddNewRow = () => {
    if ("procedureNumber" in editedRule) {
      return;
    }

    const newRule = {
      id: Date.now(), // unique temp ID
      procedureNumber: "",
      description: "",
      isNew: true,
    };

    setRules((prev) => [...prev, newRule]);
    setEditedRule(newRule);
    setEditingRuleIndex(rules.length); // last index
  };

  const handleDeleteRow = async (
    indexToDelete,
    isNew,
    procedure_template_id
  ) => {
    setRules((prev) => prev.filter((_, idx) => idx !== indexToDelete));
    if (isNew) {
      if (editingRuleIndex === indexToDelete) {
        setEditingRuleIndex(null);
        setEditedRule({});
      }
    } else {
      await dispatch(deleteProcedureTemplateRule(procedure_template_id));
    }
  };

  const handleCancelRuleEdit = (index, rule) => {
    if (rule.isNew) setRules((prev) => prev.filter((_, idx) => idx !== index));

    if (editingRuleIndex === index) {
      setEditingRuleIndex(null);
      setEditedRule({});
    }
  };
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };
  const handleSelectPart = async (selectedPart) => {
    const response = await dispatch(
      getProcedureRevisionsForPart({ partId: selectedPart.part_id })
    );
    const revisionData = response.payload.results;

    // Check if any revision has status "Released"
    const hasReleasedRevision = revisionData.some((rev) =>
      rev.status_changes?.some(
        (sc) => sc.status_changed_to?.toLowerCase() === "released"
      )
    );

    if (!hasReleasedRevision) {
      showSnackbar("This part doesn't have any 'Released' revision.", "error");

      return;
    }

    // Proceed if a released revision exists
    if (editingRowIndex !== null) {
      const updatedRules = [...rules];
      const nRule = {
        ...updatedRules[editingRowIndex],
        part_id: selectedPart.part_id,
        procedureNumber: selectedPart.part_number,
        description: selectedPart.description,
        isNew: true,
      };
      updatedRules[editingRowIndex] = nRule;
      setRules(updatedRules);
      setPartsModalOpen(false); // Close modal after selection
      setEditingRowIndex(null); // Reset editing index
      setEditedRule({ ...nRule });
    }
  };

  const oldRules = structuredClone(rules);
  const [defaultIndex, setDefaultIndex] = useState(0);

  const handleSave = () => {
    const rulesData = rules.map((r, index) => {
      return r.isNew
        ? {
            procedure_template_id: null,
            type_id_ref: rule.type_id,
            procedure_id_ref: r.part_id, // Ensure a unique identifier
          }
        : {
            procedure_template_id: r.procedure_template_id,
            type_id_ref: r.type_id_ref,
            procedure_id_ref: r.procedure_id_ref, // Ensure a unique identifier
            is_default_template: r.is_default_template,
          };
    });

    const requestData = {
      type_id_ref: rule.type_id,
      is_blank_template_permitted: true,
      rules_data: rulesData.filter((rev) => rev.procedure_id_ref !== undefined), // This filter removes blank template array
      remove_procedures: [],
    };

    requestData.rules_data = requestData.rules_data.map((d) => ({
      ...d,
      is_default_template: false,
    }));

    if (defaultIndex !== 0) {
      requestData.rules_data[defaultIndex - 1].is_default_template = true;
      requestData.is_blank_template_permitted = false;
    }

    if (rules.length > 1 && noblankRef?.current.checked) {
      requestData.is_blank_template_permitted = null;
    }

    if (requestData.rules_data.length === 0)
      requestData.is_blank_template_permitted = true;

    oldRules.forEach((r, i) => {
      if (i > 0) {
        const rl = requestData.rules_data.find(
          (cr) => cr.procedure_id_ref === r.procedure_id_ref
        );
        if (!rl) requestData.remove_procedures.push(r.procedure_template_id);
      }
    });
    setShowLoader(true);
    dispatch(
      updateProcedureTemplateRuleMultiData({ id: rule.id, data: requestData })
    ).then(() => {
      setShowLoader(false);
      navigate(-1);
    });
  };

  useEffect(() => {
    if (localSortConfig.key === null) {
      setSortedTempRules(rules);
      return;
    } else {
      const dt = SortTableData(
        rules,
        localSortConfig.direction,
        localSortConfig.key
      );
      setSortedTempRules(dt);
    }
  }, [rules, localSortConfig]);
  return (
    <div>
      <AppBar
        title={`Edit Document Template Rule for Type ${procedureType}`}
        showTablesDropdown={false}
        showProfileButton={false}
        buttons={[
          {
            label: "Save",
            onClick: canEdit ? handleSave : undefined,
            disabled: !canEdit,
            title: !canEdit
              ? "You do not have permission to save template rules."
              : undefined,
          },
        ]}
      />
      <div className="form-container">
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <label style={{ fontWeight: "bold" }}>
            Document Type (Read Only)
          </label>
          <input
            type="text"
            style={{ width: "30%", marginTop: "-8px" }}
            value={procedureType}
            onChange={(e) => setProcedureType(e.target.value)}
            disabled
          />
        </div>
        <h3 style={{ fontSize: "14px", marginTop: "30px" }}>Rules</h3>

        <AppTable
          ref={tableRef}
          showSearchInput={false}
          columnsData={columnData}
          paginate={false}
          setLocalSortConfig={setLocalSortConfig}
          extrapadding="0px"
        >
          {sortedTempRules.map((rls, index) => {
            return (
              <tr key={rls.procedure_id_ref}>
                <td>
                  <div className="input-with-icon">
                    {editingRuleIndex !== index ? (
                      rls.procedureNumber
                    ) : (
                      <div className="input-with-icon">
                        <input
                          type="text"
                          style={{
                            borderRadius: "5px 0px 0px 5px",
                            borderRight: "none",
                          }}
                          value={rls.procedureNumber}
                          readOnly
                        />
                        {rls.procedureNumber.includes("Blank") ? null : (
                          <button
                            type="button"
                            style={{
                              borderRight: "1px solid rgb(215, 215, 215)",
                              borderTop: "1px solid rgb(215, 215, 215)",
                              borderBottom: "1px solid rgb(215, 215, 215)",
                              borderLeft: "none",
                              borderRadius: "0px 5px 5px 0px",
                              padding: "8.1px 4px",
                              background: "#e9e9e9",
                            }}
                            className=""
                            onClick={() => handleEditProcedureNumber(index)}
                          >
                            <svg
                              width="15"
                              height="15"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M17.8376 6.76712L13.1867 2.16438L14.7187 0.630137C15.1382 0.210046 15.6535 0 16.2645 0C16.8755 0 17.3907 0.210046 17.8102 0.630137L19.3423 2.16438C19.7618 2.58447 19.9806 3.09132 19.9989 3.68493C20.0171 4.27854 19.8165 4.78539 19.397 5.20548L17.8376 6.76712ZM16.2508 8.38356L4.6509 20H0V15.3425L11.5999 3.72603L16.2508 8.38356Z"
                                fill="#0D6EFD"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </td>

                <td>
                  <RenderHtml
                    content={rls.description}
                    {...(editingRuleIndex === index && {
                      inptDec: true,
                      customeHeight: "24px",
                    })}
                  />
                </td>

                <td style={{ verticalAlign: "middle" }}>
                  <label
                    className="checkbox-label"
                    style={{ marginLeft: "-35px" }}
                  >
                    <input
                      type="radio"
                      name="detemplate"
                      defaultChecked={rls.is_default_template}
                      onChange={() => setDefaultIndex(index)}
                      disabled={
                        editingRuleIndex !== null ||
                        (rule.is_blank_template_permitted === null &&
                          index === 0)
                      }
                    />
                    <span style={{ marginLeft: "-50px", marginTop: "2px" }}>
                      Yes
                    </span>
                  </label>
                </td>

                <td>
                  {rls.procedureNumber.includes("Blank") ? (
                    <div className="form-check">
                      <label
                        className="form-check-label"
                        htmlFor="noblank"
                        style={{ fontWeight: "normal" }}
                      >
                        <input
                          ref={noblankRef}
                          type="checkbox"
                          id="noblank"
                          disabled={rules.length === 1}
                          defaultChecked={
                            rule.is_blank_template_permitted === null
                          }
                        />
                        No Blank Template for this Type
                      </label>
                    </div>
                  ) : (
                    <div className="app-action-btns">
                      {editingRuleIndex === index ? (
                        <>
                          <button
                            className="save-row"
                            onClick={() => {
                              if (rls.procedureNumber?.length === 0) {
                                showSnackbar(
                                  "Please select a Document Number",
                                  "error"
                                );
                                return;
                              }
                              setEditingRuleIndex(null);
                              setEditedRule({});
                            }}
                          >
                            ✔
                          </button>
                          <button
                            className="cancel-row"
                            onClick={() => {
                              handleCancelRuleEdit(index, rls);
                            }}
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="edit-row"
                            onClick={() => {
                              setEditingRuleIndex(index);
                              setEditedRule(rls);
                            }}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 15 15"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M13.4192 5.57534L9.93101 2.12329L11.0801 0.972603C11.3947 0.657534 11.7811 0.5 12.2394 0.5C12.6976 0.5 13.084 0.657534 13.3987 0.972603L14.5477 2.12329C14.8623 2.43836 15.0265 2.81849 15.0402 3.2637C15.0538 3.7089 14.9034 4.08904 14.5888 4.40411L13.4192 5.57534ZM12.2291 6.78767L3.52919 15.5H0V12.0068L8.74093 3.29452L12.2291 6.78767Z"
                                fill="white"
                              />
                            </svg>
                          </button>
                          <button
                            className="delete-row"
                            onClick={() =>
                              handleDeleteRow(
                                index,
                                rls.isNew,
                                rls.procedure_template_id
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faTrashCan} />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </AppTable>

        {isPartsModalOpen && (
          <PartsPopup
            checkRevReleased={true}
            items_type={"is_procedure"}
            setPartsModalOpen={setPartsModalOpen}
            handleSelectPart={handleSelectPart}
          />
        )}
        <button className="add-row-button" onClick={handleAddNewRow}>
          + Add Row
        </button>
      </div>

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
      {showLoader && <Loader />}
    </div>
  );
};

export default EditProcedureTemplateRule;
